import axios, { Method } from 'axios';
import { getAuth } from '../providers/AppAuthProvider';
import { API_BASE_URL as baseUrl } from 'src/utils/api-url';

/**
 * Client for the two-person request ledger (XBIT-4530). Every money-moving operation is a request that
 * another admin approves; the server executes it at most once. Used by finance transfers, allowlist,
 * limits, blind-box payout replace and world-cup amount override.
 */

export type MoneyRequestStatus =
  | 'PENDING'
  | 'CANCELLED'
  | 'REJECTED'
  | 'EXPIRED'
  | 'EXECUTING'
  | 'EXECUTED'
  | 'FAILED'
  | 'UNKNOWN';

export interface MoneyRequest {
  id: string;
  kind: string;
  status: MoneyRequestStatus;
  payload: Record<string, any>;
  requestedBy: { id: string; username: string };
  requestReason: string | null;
  decidedBy: { id: string; username: string } | null;
  decisionReason: string | null;
  decidedAt: string | null;
  expiresAt: string;
  executedAt: string | null;
  txHash: string | null;
  result: any;
  error: string | null;
  /** EXECUTING for more than 10 minutes: the process died or hung; treated like UNKNOWN. */
  stalled: boolean;
  createdAt: string;
}

export interface MoneyRequestPage {
  data: MoneyRequest[];
  total: number;
}

export interface MoneyApiError extends Error {
  /** API error code, e.g. SELF_APPROVAL_FORBIDDEN. */
  code?: string;
  /** HTTP status; undefined for a network error or a timeout (no answer). */
  status?: number;
}

async function authHeaders() {
  const { accessToken } = (await getAuth()) || {};
  return {
    Authorization: `Bearer ${accessToken}`,
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
}

/** Turn an axios failure into an Error that shows the API `code` and `message`. */
export function toMoneyApiError(e: any): MoneyApiError {
  const body = e?.response?.data;
  const code: string | undefined = body?.code;
  const message: string = body?.message || e?.message || 'Request failed';
  const err = new Error(code ? `${code}: ${message}` : message) as MoneyApiError;
  err.code = code;
  err.status = e?.response?.status;
  return err;
}

export async function moneyCall<T>(
  method: Method,
  path: string,
  body?: unknown,
  params?: Record<string, unknown>
): Promise<T> {
  try {
    const { data } = await axios.request<T>({
      method,
      url: `${baseUrl}/${path}`,
      data: body,
      params,
      headers: await authHeaders(),
    });
    return data;
  } catch (e) {
    throw toMoneyApiError(e);
  }
}

/** The six verbs every request kind shares, rooted at `base` (a path under the API prefix). */
export function moneyRequestApi(base: string) {
  return {
    list: (params?: { page?: number; size?: number; status?: string }) =>
      moneyCall<MoneyRequestPage>('GET', base, undefined, params),
    get: (id: string) => moneyCall<MoneyRequest>('GET', `${base}/${id}`),
    create: (body: unknown, path = base) => moneyCall<MoneyRequest>('POST', path, body),
    cancel: (id: string, reason?: string) =>
      moneyCall<MoneyRequest>('POST', `${base}/${id}/cancel`, reason ? { reason } : {}),
    approve: (id: string, reason?: string) =>
      moneyCall<MoneyRequest>('POST', `${base}/${id}/approve`, reason ? { reason } : {}),
    reject: (id: string, reason: string) =>
      moneyCall<MoneyRequest>('POST', `${base}/${id}/reject`, { reason }),
  };
}
